import { useCallback, useEffect, useMemo, useState } from "react";
import debounce from "lodash/debounce";
import Transport from "../../../../../../passapp-sdk/transport";
import { TransportCompany } from "../../../../../../passapp-sdk/types/transportTypes";
import { useStore } from "../../../../../../hooks/useStore";

type CreateCompanyResult =
  | { success: true; company: TransportCompany }
  | { success: false; error: string };

export const useTransportCompanies = () => {
  const { authStore } = useStore();
  const transportApi = useMemo(() => new Transport(), []);

  const [companies, setCompanies] = useState<TransportCompany[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const fetchCompanies = useCallback(
    debounce(async (searchQuery: string, token: string) => {
      setError(null);
      try {
        const response = await transportApi.getTransportCompanies(token, searchQuery);
        setCompanies(response?.data ?? []);
      } catch (err) {
        console.error("Error fetching transport companies:", err);
        setError(typeof err === "string" ? err : "Error al buscar empresas transportistas");
        setCompanies([]);
      } finally {
        setIsLoading(false);
      }
    }, 350),
    [transportApi]
  );

  useEffect(() => {
    if (!authStore.token) return;
    setIsLoading(true);
    fetchCompanies(search, authStore.token);
    return () => fetchCompanies.cancel();
  }, [search, authStore.token, fetchCompanies]);

  const createCompany = useCallback(
    async (name: string): Promise<CreateCompanyResult> => {
      if (!authStore.token) {
        return { success: false, error: "Sesión inválida" };
      }
      setIsCreating(true);
      try {
        const response = await transportApi.addTransportCompany(authStore.token, name);
        const created: TransportCompany = {
          id: response.data.id,
          name: response.data.name,
          description: "",
          contactEmails: [],
        };
        setCompanies((prev) => [created, ...prev]);
        return { success: true, company: created };
      } catch (err) {
        console.error("Error creating transport company:", err);
        return {
          success: false,
          error: typeof err === "string" ? err : "Error al crear la empresa transportista",
        };
      } finally {
        setIsCreating(false);
      }
    },
    [authStore.token, transportApi]
  );

  return { companies, isLoading, error, search, setSearch, isCreating, createCompany };
};

export default useTransportCompanies;
